import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { subscription as subApi } from "@/lib/api";
import { Check, Crown, Loader2 } from "lucide-react";

const SubscriptionCTA = ({ onOpenAuth }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const benefits = ["b1", "b2", "b3", "b4", "b5"];

  const handleSubscribe = async () => {
    if (!user) {
      onOpenAuth?.("register");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await subApi.create(window.location.origin);
      if (res.approve_url) {
        window.location.href = res.approve_url;
      } else {
        setError(t("subscription.error"));
        setLoading(false);
      }
    } catch {
      setError(t("subscription.error"));
      setLoading(false);
    }
  };

  return (
    <section id="premium" data-testid="subscription-section" className="bg-gradient-to-br from-indigo-950 via-slate-900 to-emerald-950 text-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="inline-flex items-center gap-2 border border-gold px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-bold mb-6 text-[#E8C14E]">
              <Crown className="w-3.5 h-3.5" /> {t("subscription.tag")}
            </div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-6">
              {t("subscription.title1")}
              <br />
              {t("subscription.title2")}
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed max-w-lg">
              {t("subscription.subtitle")}
            </p>
          </div>

          <div className="bg-white text-zinc-900 p-8 md:p-10">
            <div className="flex items-end gap-2 mb-8">
              <span className="font-display font-black text-6xl tracking-tight gold-gradient-text">$19</span>
              <span className="text-zinc-500 mb-2 font-semibold">USD {t("subscription.perMonth")}</span>
            </div>

            <ul className="space-y-3 mb-8">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm">
                  <Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                  {t(`subscription.${b}`)}
                </li>
              ))}
            </ul>

            {user?.is_subscriber ? (
              <div>
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold p-4 mb-4 text-center">
                  ✓ {t("subscription.active")}
                </div>
                <button
                  onClick={() => navigate("/dashboard")}
                  data-testid="subscription-dashboard-btn"
                  className="w-full bg-zinc-900 hover:bg-zinc-800 text-white py-4 font-semibold transition"
                >
                  {t("subscription.goDashboard")}
                </button>
              </div>
            ) : (
              <button
                onClick={handleSubscribe}
                disabled={loading}
                data-testid="subscribe-paypal-btn"
                className="w-full bg-[#FFC439] hover:bg-[#F2B82E] text-zinc-900 py-4 font-bold transition flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> {t("subscription.processing")}
                  </>
                ) : (
                  t("subscription.cta")
                )}
              </button>
            )}

            {error && (
              <p data-testid="subscription-error" className="text-red-600 text-sm mt-3 text-center">
                {error}
              </p>
            )}
            <p className="text-xs text-zinc-500 mt-4 text-center">{t("subscription.guarantee")}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubscriptionCTA;
